import React, { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { Trans, useTranslation } from "react-i18next";
import { getValueFromInput } from "util/FormsUtils";
import { Input, Button, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup } from "components/bootstrap";
import { ColorPickerPopover, TimezoneSelect } from "components/common";
import ColorLabel from "components/sidecars/common/ColorLabel";
import DocsHelper from "util/DocsHelper";
import DocumentationLink from "components/support/DocumentationLink";
export const defaultConfig = {
  color: "#FF0000",
  webhook_url: "",
  /* eslint-disable no-template-curly-in-string */
  custom_message: "<b>--- [Event Definition] ---</b>\n<table>\n<tr><td><b>Title:</b></td><td>${event_definition_title}</td></tr>\n<tr><td><b>Type:</b></td><td>${event_definition_type}</td></tr>\n<table>\n\n<b>--- [Event] ---</b>\n<table>\n<tr><td><b>Alert Replay:</b></td><td>${http_external_uri}alerts/${event.id}/replay-search</td></tr>\n<tr><td><b>Timestamp:</b></td><td>${event.timestamp}</td></tr>\n<tr><td><b>Message:</b></td><td>${event.message}</td></tr>\n<tr><td><b>Source:</b></td><td>${event.source}</td></tr>\n<tr><td><b>Key:</b></td><td>${event.key}</td></tr>\n<tr><td><b>Priority:</b></td><td>${event.priority}</td></tr>\n<tr><td><b>Alert:</b></td><td>${event.alert}</td></tr>\n<tr><td><b>Timestamp Processing:</b></td><td>${event.timestamp}</td></tr>\n<tr><td><b>Timerange Start:</b></td><td>${event.timerange_start}</td></tr>\n<tr><td><b>Timerange End:</b></td><td>${event.timerange_end}</td></tr>\n<table>\n\n<b>Event Fields:</b>\n<table>\n${foreach event.fields field}\n<tr><td><b>${field.key}:</b></td><td>${field.value}</td></tr>\n${end}\n</table>\n\n${if backlog}\n<b>--- [Backlog] ---</b>\n${foreach backlog message}\n<p><code>${message.timestamp}  ::  ${message.source}  ::  ${message.message}</code></p>\n${end}${end}",
  /* eslint-enable no-template-curly-in-string */
  icon_url: "",
  backlog_size: 0,
  time_zone: "UTC"
};
const TeamsNotificationForm = ({ config, validation, onChange }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const { t } = useTranslation("integrations");
  const defaultBacklogSize = config.backlog_size;
  const [isBacklogSizeEnabled, setIsBacklogSizeEnabled] = useState(defaultBacklogSize > 0);
  const [backlogSize, setBacklogSize] = useState(defaultBacklogSize);
  const propagateChange = (key, value) => {
    const nextConfig = cloneDeep(config);
    nextConfig[key] = value;
    onChange(nextConfig);
  };
  const handleBacklogSizeChange = (event) => {
    const { name } = event.target;
    const value = Number(getValueFromInput(event.target));
    setBacklogSize(value);
    propagateChange(name, value);
  };
  const toggleBacklogSize = () => {
    setIsBacklogSizeEnabled((prevIsBacklogSizeEnabled) => {
      const nextIsBacklogSizeEnabled = !prevIsBacklogSizeEnabled;
      propagateChange("backlog_size", prevIsBacklogSizeEnabled ? 0 : backlogSize);
      return nextIsBacklogSizeEnabled;
    });
  };
  const handleColorChange = (color, _, hidePopover) => {
    hidePopover();
    propagateChange("color", color);
  };
  const handleTimeZoneChange = (nextValue) => {
    propagateChange("time_zone", nextValue);
  };
  const handleChange = (event) => {
    const { name } = event.target;
    propagateChange(name, getValueFromInput(event.target));
  };
  const element = /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "slack_notification_form.custom_message_help",
      components: {
        docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.ALERTS, text: t("slack_notification_form.docs_link_text") })
      }
    }
  ));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "color" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("slack_notification_form.configuration_color_label")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(ColorLabel, { color: config.color }), /* @__PURE__ */ React.createElement("div", { style: { display: "inline-block", marginLeft: 15 } }, /* @__PURE__ */ React.createElement(
    ColorPickerPopover,
    {
      id: "color",
      color: config.color || "#f06292",
      placement: "right",
      triggerNode: /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall" }, t("slack_notification_form.change_color_button")),
      onChange: handleColorChange
    }
  ))), /* @__PURE__ */ React.createElement(HelpBlock, null, t("slack_notification_form.color_help"))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-webhookUrl",
      name: "webhook_url",
      label: t("teams_notification_details.webhook_url_label"),
      type: "text",
      bsStyle: validation.errors.webhook_url ? "error" : null,
      help: (_c = (_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.webhook_url) == null ? void 0 : _b[0]) != null ? _c : t("teams_notification_form.webhook_url_help"),
      value: config.webhook_url || "",
      onChange: handleChange,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-customMessage",
      name: "custom_message",
      label: t("slack_notification_form.custom_message_label"),
      type: "textarea",
      bsStyle: validation.errors.custom_message ? "error" : null,
      help: (_f = (_e = (_d = validation == null ? void 0 : validation.errors) == null ? void 0 : _d.custom_message) == null ? void 0 : _e[0]) != null ? _f : element,
      value: config.custom_message || "",
      onChange: handleChange
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-time-zone",
      help: t("slack_notification_form.time_zone_help"),
      label: t("slack_notification_form.time_zone_label")
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        className: "timezone-select",
        name: "time_zone",
        value: config.time_zone,
        onChange: handleTimeZoneChange,
        clearable: false
      }
    )
  ), /* @__PURE__ */ React.createElement(ControlLabel, null, t("slack_notification_form.backlog_limit_label")), /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Addon, null, /* @__PURE__ */ React.createElement(
    "input",
    {
      id: "toggle_backlog_size",
      type: "checkbox",
      checked: isBacklogSizeEnabled,
      onChange: toggleBacklogSize
    }
  )), /* @__PURE__ */ React.createElement(
    FormControl,
    {
      type: "number",
      id: "backlog_size",
      name: "backlog_size",
      onChange: handleBacklogSizeChange,
      value: backlogSize,
      min: "0",
      disabled: !isBacklogSizeEnabled
    }
  )), /* @__PURE__ */ React.createElement(HelpBlock, null, t("teams_notification_form.backlog_limit_help"))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-iconUrl",
      name: "icon_url",
      label: t("slack_notification_form.icon_url_label"),
      type: "text",
      bsStyle: validation.errors.icon_url ? "error" : null,
      help: (_i = (_h = (_g = validation == null ? void 0 : validation.errors) == null ? void 0 : _g.icon_url) == null ? void 0 : _h[0]) != null ? _i : t("slack_notification_form.icon_url_help"),
      value: config.icon_url || "",
      onChange: handleChange
    }
  ));
};
export default TeamsNotificationForm;
